import React, { useState, useEffect } from 'react';
import API from '../utils/API';
import { getIssueIcon } from '../utils/issueIcons';
import IconPickerModal from '../components/IconPickerModal';

export default function AdminIssueAreas() {
    const [issues, setIssues] = useState([]);
    const [loading, setLoading] = useState(true);

    // Rename state
    const [editingId, setEditingId] = useState(null);
    const [editDraft, setEditDraft] = useState('');
    const [editError, setEditError] = useState('');
    const [saving, setSaving] = useState({});

    // Icon state
    const [pickerIssue, setPickerIssue] = useState(null);
    const [pickerSaving, setPickerSaving] = useState(false);
    const [pickerError, setPickerError] = useState('');

    // Delete state
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);

    // Add new state
    const [newName, setNewName] = useState('');
    const [addError, setAddError] = useState('');
    const [adding, setAdding] = useState(false);

    // Consolidate state
    const [selected, setSelected] = useState(new Set());
    const [consolidateName, setConsolidateName] = useState('');
    const [consolidating, setConsolidating] = useState(false);
    const [consolidateError, setConsolidateError] = useState('');

    const load = async () => {
        setLoading(true);
        try {
            const res = await API.getAdminIssueAreas();
            setIssues(res.data);
        } catch (err) {
            console.error('Failed to load issue areas:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    // ── Add new ───────────────────────────────────────────────
    const handleAdd = async (e) => {
        e.preventDefault();
        const name = newName.trim();
        if (!name) return;
        if (issues.some(i => i.issue_name.toLowerCase() === name.toLowerCase())) {
            setAddError('An issue area with that name already exists.');
            return;
        }
        setAdding(true);
        setAddError('');
        try {
            await API.addIssueArea({ issue_name: name });
            setNewName('');
            await load();
        } catch (err) {
            setAddError('Failed to add issue area.');
        } finally {
            setAdding(false);
        }
    };

    // ── Rename ────────────────────────────────────────────────
    const startEdit = (issue) => {
        setEditingId(issue.issue_id);
        setEditDraft(issue.issue_name);
        setEditError('');
        setConfirmDeleteId(null);
        setPickerIssue(null);
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditDraft('');
        setEditError('');
    };

    const saveEdit = async (issue_id) => {
        const name = editDraft.trim();
        if (!name) return;
        if (issues.some(i => i.issue_id !== issue_id && i.issue_name.toLowerCase() === name.toLowerCase())) {
            setEditError('Another issue area already has that name.');
            return;
        }
        setSaving(s => ({ ...s, [issue_id]: true }));
        setEditError('');
        try {
            await API.updateIssueArea({ issue_id, issue_name: name });
            cancelEdit();
            await load();
        } catch (err) {
            setEditError('Failed to save.');
        } finally {
            setSaving(s => ({ ...s, [issue_id]: false }));
        }
    };

    // ── Icon ──────────────────────────────────────────────────
    const openIconPicker = (issue) => {
        setPickerIssue(issue);
        setPickerError('');
        setEditingId(null);
        setConfirmDeleteId(null);
    };

    const closeIconPicker = () => {
        setPickerIssue(null);
        setPickerError('');
    };

    const handleIconSelect = async (issue_icon) => {
        if (!pickerIssue) return;
        setPickerSaving(true);
        setPickerError('');
        try {
            await API.updateIssueArea({ issue_id: pickerIssue.issue_id, issue_icon });
            await load();
            closeIconPicker();
        } catch (err) {
            setPickerError('Failed to save icon.');
        } finally {
            setPickerSaving(false);
        }
    };

    // ── Delete ────────────────────────────────────────────────
    const confirmDelete = async (issue_id) => {
        setSaving(s => ({ ...s, [`del_${issue_id}`]: true }));
        try {
            await API.deleteIssueArea({ issue_id });
            setConfirmDeleteId(null);
            setSelected(prev => { const next = new Set(prev); next.delete(issue_id); return next; });
            setIssues(prev => prev.filter(i => i.issue_id !== issue_id));
        } catch (err) {
            console.error('Failed to delete issue area:', err);
        } finally {
            setSaving(s => ({ ...s, [`del_${issue_id}`]: false }));
        }
    };

    // ── Consolidate ───────────────────────────────────────────
    const toggleSelect = (issue_id) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(issue_id)) {
                next.delete(issue_id);
            } else {
                next.add(issue_id);
            }
            return next;
        });
        setConsolidateError('');
    };

    const clearSelection = () => {
        setSelected(new Set());
        setConsolidateName('');
        setConsolidateError('');
    };

    const handleConsolidate = async () => {
        const name = consolidateName.trim();
        if (!name) { setConsolidateError('Enter a name for the consolidated issue area.'); return; }
        setConsolidating(true);
        setConsolidateError('');
        try {
            await API.consolidateIssueAreas({ source_ids: [...selected], target_name: name });
            clearSelection();
            await load();
        } catch (err) {
            setConsolidateError('Consolidation failed.');
        } finally {
            setConsolidating(false);
        }
    };

    const selectedIssues = issues.filter(i => selected.has(i.issue_id));

    return (
        <div>
            {/* ── Add new ── */}
            <form className="ai-add-row" onSubmit={handleAdd}>
                <input
                    className="admin-search-input"
                    placeholder="New issue area name…"
                    value={newName}
                    onChange={e => { setNewName(e.target.value); setAddError(''); }}
                />
                <button className="awards-save-btn" type="submit" disabled={adding || !newName.trim()}>
                    {adding ? <i className="bi bi-arrow-repeat admin-spin" /> : 'Add'}
                </button>
            </form>
            {addError && <div className="ai-error mb-2">{addError}</div>}

            {/* ── Consolidate panel ── */}
            {selected.size >= 2 && (
                <div className="ai-consolidate-panel">
                    <div className="ai-consolidate-title">
                        Consolidate {selected.size} selected issue areas
                    </div>
                    <div className="ai-consolidate-chips">
                        {selectedIssues.map(i => (
                            <span key={i.issue_id} className="ai-consolidate-chip">
                                {i.issue_name}
                                <i className="bi bi-x ms-1" onClick={() => toggleSelect(i.issue_id)} />
                            </span>
                        ))}
                    </div>
                    <div className="ai-consolidate-name-row">
                        <label className="ai-consolidate-label">Consolidated name:</label>
                        <div className="ai-consolidate-presets">
                            {selectedIssues.map(i => (
                                <button
                                    key={i.issue_id}
                                    type="button"
                                    className={`ai-preset-btn ${consolidateName === i.issue_name ? 'active' : ''}`}
                                    onClick={() => setConsolidateName(i.issue_name)}
                                >
                                    {i.issue_name}
                                </button>
                            ))}
                        </div>
                        <input
                            className="admin-search-input ai-consolidate-input"
                            placeholder="Or type a new name…"
                            value={consolidateName}
                            onChange={e => { setConsolidateName(e.target.value); setConsolidateError(''); }}
                        />
                    </div>
                    {consolidateError && <div className="ai-error mb-1">{consolidateError}</div>}
                    <div className="ai-consolidate-actions">
                        <button
                            className="awards-save-btn"
                            onClick={handleConsolidate}
                            disabled={consolidating || !consolidateName.trim()}
                        >
                            {consolidating ? <i className="bi bi-arrow-repeat admin-spin" /> : 'Consolidate'}
                        </button>
                        <button className="awards-cancel-btn" onClick={clearSelection}>
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* ── Issue list ── */}
            {loading ? (
                <div className="admin-loading">Loading…</div>
            ) : (
                <div className="admin-table-wrapper">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th style={{ width: '2rem' }}>
                                    <input
                                        type="checkbox"
                                        checked={selected.size === issues.length && issues.length > 0}
                                        onChange={e => {
                                            if (e.target.checked) setSelected(new Set(issues.map(i => i.issue_id)));
                                            else clearSelection();
                                        }}
                                        title="Select all"
                                    />
                                </th>
                                <th className="admin-th-center">Icon</th>
                                <th>Issue Area</th>
                                <th className="admin-th-center">Profiles</th>
                                <th className="admin-th-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {issues.length === 0 ? (
                                <tr><td colSpan={5} className="admin-empty">No issue areas found</td></tr>
                            ) : issues.map(issue => (
                                <tr key={issue.issue_id} className={selected.has(issue.issue_id) ? 'ai-row-selected' : ''}>
                                    <td className="admin-td-check">
                                        <input
                                            type="checkbox"
                                            checked={selected.has(issue.issue_id)}
                                            onChange={() => toggleSelect(issue.issue_id)}
                                        />
                                    </td>
                                    <td className="admin-td-center" data-label="Icon">
                                        <span
                                            className="ai-icon-cell"
                                            onClick={() => openIconPicker(issue)}
                                            title="Click to change icon"
                                        >
                                            <i className={`bi ${issue.issue_icon || getIssueIcon(issue.issue_name)}`} />
                                        </span>
                                    </td>
                                    <td data-label="Issue Area">
                                        {editingId === issue.issue_id ? (
                                            <div className="admin-email-edit">
                                                <input
                                                    className="admin-email-input"
                                                    value={editDraft}
                                                    onChange={e => { setEditDraft(e.target.value); setEditError(''); }}
                                                    onKeyDown={e => {
                                                        if (e.key === 'Enter') saveEdit(issue.issue_id);
                                                        if (e.key === 'Escape') cancelEdit();
                                                    }}
                                                    autoFocus
                                                    style={{ width: '100%', maxWidth: 260 }}
                                                />
                                                <div className="admin-email-actions">
                                                    <button
                                                        className="admin-email-save"
                                                        onClick={() => saveEdit(issue.issue_id)}
                                                        disabled={saving[issue.issue_id]}
                                                        title="Save"
                                                    >
                                                        {saving[issue.issue_id]
                                                            ? <i className="bi bi-arrow-repeat admin-spin" />
                                                            : <i className="bi bi-check-lg" />}
                                                    </button>
                                                    <button className="admin-email-cancel" onClick={cancelEdit} title="Cancel">
                                                        <i className="bi bi-x-lg" />
                                                    </button>
                                                </div>
                                                {editError && <div className="admin-email-error">{editError}</div>}
                                            </div>
                                        ) : (
                                            <span className="admin-name">{issue.issue_name}</span>
                                        )}
                                    </td>
                                    <td className="admin-td-center" data-label="Profiles" style={{ color: '#5a6a72', fontSize: '0.82rem' }}>
                                        {issue.profile_count}
                                    </td>
                                    <td className="admin-td-center" data-label="Actions">
                                        {confirmDeleteId === issue.issue_id ? (
                                            <div className="ai-delete-confirm">
                                                <span className="ai-delete-confirm-msg">
                                                    Delete{issue.profile_count > 0 ? ` (removes from ${issue.profile_count} profile${issue.profile_count > 1 ? 's' : ''})` : ''}?
                                                </span>
                                                <button
                                                    className="awards-btn awards-btn--delete ai-action-btn"
                                                    onClick={() => confirmDelete(issue.issue_id)}
                                                    disabled={saving[`del_${issue.issue_id}`]}
                                                >
                                                    {saving[`del_${issue.issue_id}`]
                                                        ? <i className="bi bi-arrow-repeat admin-spin" />
                                                        : 'Yes, delete'}
                                                </button>
                                                <button
                                                    className="awards-btn ai-action-btn"
                                                    onClick={() => setConfirmDeleteId(null)}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="ai-action-group">
                                                <button
                                                    className="awards-btn awards-btn--edit ai-action-btn"
                                                    onClick={() => startEdit(issue)}
                                                    title="Rename"
                                                >
                                                    <i className="bi bi-pencil me-1" />Rename
                                                </button>
                                                <button
                                                    className="awards-btn awards-btn--delete ai-action-btn"
                                                    onClick={() => { setConfirmDeleteId(issue.issue_id); setEditingId(null); }}
                                                    title="Delete"
                                                >
                                                    <i className="bi bi-trash me-1" />Delete
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {selected.size === 1 && (
                <div className="ai-select-hint">Select one more issue area to consolidate.</div>
            )}

            <IconPickerModal
                show={!!pickerIssue}
                value={pickerIssue ? (pickerIssue.issue_icon || getIssueIcon(pickerIssue.issue_name)) : ''}
                onSelect={handleIconSelect}
                onClose={closeIconPicker}
                saving={pickerSaving}
                error={pickerError}
            />
        </div>
    );
}
